import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';

import Layout from '../layouts/Default';
import SEO from '../components/Seo';

import { cleanMetatags, getAlternateLinks, dynamicSrcSet } from '../ui/helpers';
import { destroyWebgl, initializeWebgl } from '../ui/homeWebgl';

import HomeBillboard from '../components/HomeBillboard';
import HomeFullVideo from '../components/HomeFullVideo';
import CoursesPath from '../components/HomeCourses';
import HomeImageText from '../components/HomeImageText';
import HomeKPI from '../components/HomeKPI';
import HomeNews from '../components/HomeNews';

interface IProps {
  data: any;
  pageContext: {
    id: string;
    langcode: string;
    country: string;
    translations: any;
  };
}

const Home = ({ data, pageContext }: IProps) => {
  const { nodeHome } = data;

  if (!nodeHome) return <h2>Under Construction</h2>;

  useEffect(() => {
    initializeWebgl();

    return destroyWebgl;
  }, []);

  const regularMetatags = nodeHome?.metatag_normalized
    ? cleanMetatags(nodeHome.metatag_normalized)
    : null;
  const alternateLinks = nodeHome?.metatag_normalized
    ? getAlternateLinks(nodeHome.metatag_normalized)
    : null;
  const components = nodeHome;
  const relationships = components?.relationships;
  const news = relationships?.field_news.length ? relationships?.field_news : null;
  const hellowordField = relationships?.field_helloword?.[0];
  const videoUrl = hellowordField?.relationships?.field_video?.field_media_oembed_video || '';


  return (
    <Layout
      alternates={alternateLinks}
      lang={pageContext.langcode}
      country={pageContext.country}
      countryCode={pageContext.countryCode}
      translations={pageContext.translations}
      pageCta={nodeHome?.field_cta}>
      <SEO
        title={nodeHome.title}
        lang={nodeHome.langcode}
        metas={[...regularMetatags, ...alternateLinks]}
      />
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={
            relationships?.field_billboard_image?.relationships?.field_media_image?.drupal_links
              ?.original?.href
          }
          imagesrcset={dynamicSrcSet(
            [
              [
                relationships?.field_billboard_image?.relationships?.field_media_image
                  ?.drupal_links,
                ['large', 'medium', 'xlarge']
              ]
            ],
            true
          )}
          imagesizes="(-webkit-min-device-pixel-ratio: 2) 50vw, (min-resolution: 192dpi) 50vw, (min-resolution: 2dppx) 50vw, (-webkit-min-device-pixel-ratio: 3) 33.33vw, (min-resolution: 288dpi) 33.33vw, (min-resolution: 3dppx) 33.33vw"
        />
      </Helmet>
      <HomeBillboard
        title={components.title}
        claim={components.field_billboard_claim}
        desktopImage={
          relationships?.field_billboard_image?.relationships?.field_media_image?.drupal_links
        }
        mobileImage={
          relationships?.field_billboard_mobile_image?.relationships?.field_media_image
            ?.drupal_links
        }
        imageAlt={components.field_billboard_image?.field_media_image?.alt}
      />

      <HomeFullVideo  // componente field_helloword
        forceCenter={true} // prop center solo para field_helloword
        theme="dark"
        heading="heading_2"
        title={hellowordField?.field_title}
        description={hellowordField?.field_text?.processed}
        videoUrl={videoUrl}
        videoType="youtube"
        gtmAction="metodo"
      />


      <HomeFullVideo // componente field_method
        centered={true} // prop center field_method
        theme="dark"
        heading="heading_2"
        title={relationships?.field_method?.[0]?.field_title}
        description={relationships?.field_method?.[0]?.field_text?.processed}
        imageUrl={
          relationships?.field_method?.[0]?.relationships?.field_image?.relationships?.field_media_image
            ?.drupal_links
        }
        imageAlt={
          relationships?.field_method?.[0]?.relationships?.field_image?.field_media_image?.alt
        }
        cta={
          relationships?.field_method?.[0]?.field_cta?.external || relationships?.field_method?.[0]?.field_cta?.internal
            ? {
              uri: relationships?.field_method?.[0]?.field_cta?.external || '',
              title: relationships?.field_method?.[0]?.field_cta?.internal?.title || ''
            }
            : undefined
        }
        gtmAction="metodo"
      />

      <CoursesPath
        title={relationships?.field_courses?.field_title}
        description={relationships?.field_courses?.field_text?.processed}
        courses={relationships?.field_courses?.relationships?.field_courses.map((course, i) => ({
          key: course?.drupal_id + '_' + i,
          title: course?.field_title,
          color: course?.field_color,
          description: course?.field_subtitle,
          cta: { uri: course?.field_cta.external, title: course?.field_cta?.internal?.title },
          desktopImageUrl:
            course?.relationships?.field_image?.relationships?.field_media_image?.drupal_links,
          mobileImageUrl:
            course?.relationships?.field_mobile_image?.relationships?.field_media_image?.drupal_links,
          imageAlt: course?.relationships?.field_media_image?.alt
        }))}
        gtmAction="cursos"
        translations={pageContext?.translations}
      />
      <HomeImageText
        imgFullBackground={true}
        theme={relationships?.field_academies?.field_color}
        heading="heading_2"
        title={relationships?.field_academies?.field_title}
        subtitle={relationships?.field_academies?.field_academy_subtitle}
        direction="text_left"
        text={relationships?.field_academies?.field_text?.processed}
        link={{
          uri: relationships?.field_academies?.field_cta.external,
          title: relationships?.field_academies?.field_cta.internal.title
        }}
        imageUrl={
          relationships?.field_academies?.relationships?.field_image?.relationships
            ?.field_media_image?.drupal_links
        }
        imageAlt={
          relationships?.field_academies?.relationships?.field_image?.field_media_image?.alt
        }
        gtmAction="academias"
        isAcademy
      />
      <HomeImageText
        theme={relationships?.field_activities?.field_color}
        heading="heading_2"
        title={relationships?.field_activities?.field_title}
        subtitle={relationships?.field_activities?.field_pre_title}
        direction="text_right"
        text={relationships?.field_activities?.field_text.processed}
        imageUrl={
          relationships?.field_activities?.relationships?.field_activity_image?.relationships
            .field_media_image?.drupal_links
        }
        imageAlt={
          relationships?.field_activities?.relationships?.field_activity_image?.field_media_image
            ?.alt
        }
        link={{
          uri: relationships?.field_activities?.field_cta?.external,
          title: relationships?.field_activities?.field_cta?.internal?.title
        }}
        gtmAction="actividades"
        isActivities
      />
      <HomeKPI
        heading="heading_2"
        title={relationships?.field_kpis?.field_title}
        subtitle={relationships?.field_kpis?.field_kpi_subtitle}
        imageUrl={
          relationships?.field_kpis?.relationships?.field_image?.relationships?.field_media_image
            ?.drupal_links
        }
        imageAlt={
          relationships?.field_kpis?.relationships?.field_image?.relationships?.field_media_image
            ?.alt
        }
        blocks={relationships?.field_kpis?.relationships?.field_kpis.map((kpi, i) => ({
          key: kpi?.drupal_id + '_' + i,
          theme: kpi?.field_color,
          bigText: kpi?.field_title,
          smallText: kpi?.field_subtitle,
          prefix: kpi?.field_prefix,
          imageUrl: kpi?.relationships.field_kpi_item_image.relationships.field_media_image.drupal_links
        }))}
        isHome={true}
      />
      <HomeImageText
        theme={relationships?.field_franchises?.field_color}
        heading="heading_2"
        title={relationships?.field_franchises?.field_title}
        subtitle={relationships?.field_franchises?.field_franchises_subtitle}
        direction="text_left"
        text={relationships?.field_franchises?.field_text?.processed}
        link={{
          uri: relationships?.field_franchises?.field_cta.external,
          title: relationships?.field_franchises?.field_cta.internal.title
        }}
        imageUrl={
          relationships?.field_franchises?.relationships?.field_image?.relationships
            ?.field_media_image?.drupal_links
        }
        imageAlt={
          relationships?.field_franchises?.relationships?.field_image?.field_media_image?.alt
        }
        gtmAction="contacto_b2b"
        isFranchises
        isCoverImage={relationships?.field_franchises?.field_cover_image}
      />
      <HomeImageText
        theme={relationships?.field_online_shop?.field_color}
        heading="heading_2"
        title={relationships?.field_online_shop?.field_title}
        subtitle={relationships?.field_online_shop?.field_online_shop_subtitle}
        direction="text_right"
        text={relationships?.field_online_shop?.field_text?.processed}
        link={{
          uri: relationships?.field_online_shop?.field_cta.external,
          title: relationships?.field_online_shop?.field_cta.internal.title
        }}
        imageUrl={
          relationships?.field_online_shop?.relationships?.field_image?.relationships
            ?.field_media_image?.drupal_links
        }
        imageAlt={
          relationships?.field_online_shop?.relationships?.field_image?.field_media_image?.alt
        }
        gtmAction="shop"
        isShop
        isCoverImage={relationships?.field_online_shop?.field_cover_image}
      />
      {news && (
        <div><HomeNews
          title={pageContext?.translations?.centerPage?.news}
          news={news}
          gtmAction="noticias"
          allNews={{ uri: pageContext?.translations?.news?.blogUrl, title: pageContext?.translations?.news?.showAll }}
        /></div>
      )}
    </Layout>
  );
};

export const query = graphql`
  query ($id: String) {
    nodeHome(id: { eq: $id }) {
      title
      id
      drupal_id
      langcode
      status
      field_cta {
        external
        internal {
          title
        }
      }
      field_billboard_claim
      relationships {
        field_billboard_image {
          relationships {
            field_media_image {
              drupal_links {
                fullwidth {
                  href
                  meta {
                    height
                    width
                  }
                }
                large {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium {
                  href
                  meta {
                    height
                    width
                  }
                }
                original {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge {
                  href
                  meta {
                    height
                    width
                  }
                }
                fullwidth_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                large_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                original_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
              }
            }
          }
          field_media_image {
            alt
          }
        }
        field_billboard_mobile_image {
          relationships {
            field_media_image {
              drupal_links {
                fullwidth {
                  href
                  meta {
                    height
                    width
                  }
                }
                large {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium {
                  href
                  meta {
                    height
                    width
                  }
                }
                original {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge {
                  href
                  meta {
                    height
                    width
                  }
                }
                fullwidth_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                large_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                original_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
              }
            }
          }
          field_media_image {
            alt
          }
        }
        field_country {
          field_country_code
          field_domain {
            uri
          }
          relationships {
            field_languages {
              drupal_id
            }
          }
        }
        field_hreflang {
          id
          title
          langcode
        }
        field_academies {
          field_color
          field_cta {
            internal {
              title
            }
            external
          }
          field_text {
            processed
          }
          field_title
          field_academy_subtitle
          relationships {
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
        field_activities {
          field_color
          field_text {
            processed
          }
          field_title
          field_pre_title
          field_cta {
            external
            internal {
              title
              uri
            }
          }
          relationships {
            field_activity_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
        field_courses {
          id
          field_title
          field_text {
            processed
          }
          relationships {
            field_courses {
              field_cta {
                internal {
                  title
                }
                external
              }
              field_color
              drupal_id
              field_subtitle
              field_title
              relationships {
                field_mobile_image {
                  field_media_image {
                    alt
                  }
                  relationships {
                    field_media_image {
                      drupal_links {
                        large {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        large_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                      }
                    }
                  }
                }
                field_image {
                  field_media_image {
                    alt
                  }
                  relationships {
                    field_media_image {
                      drupal_links {
                        large {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        large_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        field_franchises {
          field_color
          field_cover_image
          field_cta {
            internal {
              title
            }
            external
          }
          field_text {
            processed
          }
          field_title
          field_franchises_subtitle
          relationships {
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
        field_kpis {
          field_title
          field_kpi_subtitle
          relationships {
            field_kpis {
              drupal_id
              field_color
              field_subtitle
              field_title
              field_prefix
              relationships {
                field_kpi_item_image {
                  relationships {
                    field_media_image {
                      drupal_links {
                        large {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        large_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        medium_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        original_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        small_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xlarge_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xxsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }

 field_helloword {
  field_text {
    processed
  }
  field_title
  relationships {
    field_video {
      ... on media__media_youtube {
        field_media_oembed_video
        name
      }
    }
  }
}


        field_method {
          field_cta {
            internal {
              title
            }
            external
          }
          field_text {
            processed
          }
          field_title
          relationships {
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xlarge {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    fullwidth {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    fullwidth_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
        field_online_shop {
          field_color
          field_cover_image
          field_cta {
            internal {
              title
            }
            external
          }
          field_text {
            processed
          }
          field_title
          field_online_shop_subtitle
          relationships {
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xlarge {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        width
                        height
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        },
        field_news {
          field_date(formatString: "DD/MM/YYYY")
          field_cta {
            external
            internal {
              title
            }
          }
          field_text {
            processed
          }
          field_title
          relationships {
            field_image {
              field_media_image {
                alt
              }
              relationships {
                field_media_image {
                  drupal_links {
                    large {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    large_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    medium_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    original_webp {
                      meta {
                        height
                        width
                      }
                    }
                    small {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    small_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xlarge_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall {
                      href
                      meta {
                        height
                        width
                      }
                    }
                    xxsmall_webp {
                      href
                      meta {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      metatag_normalized {
        attributes {
          content
          href
          hreflang
          name
          property
          rel
        }
      }
    }
  }
`;

export default Home;
